import React, { useEffect, useState } from 'react';
import { organisationsAPI } from '../../../../organisations/api/organisationsAPI';
import Segments from '../../overview/Segments';
import { openBuilderDocument, openDocument, openDocumentErrorMessage } from '../../overview/cells';
import MoveComparison from './MoveComparison';
import VersionTimeline from './VersionTimeline';
import { dayMonth, documentTypeLabel, plural } from './lib/activityFormat';
import {
  defaultPicks, feedbackPoints, nextPicks, pairSummary, uploadPoints,
} from './lib/documentHistory';

// Each element's history, once loaded, for as long as the page is open:
// re-expanding a card or going back to a question never fetches again.
// Keyed by student and element; a failed request is not kept, so Retry
// asks again.
const loaded = new Map();
const pending = new Map();
const cacheKey = (studentId, element) => `${studentId}\u0000${element}`;

/** Forgets every loaded history (for tests). */
export function resetHistoryCache() {
  loaded.clear();
  pending.clear();
}

const isHistory = (data) => Boolean(data) && Array.isArray(data.points) && Array.isArray(data.moves)
  && Array.isArray(data.never_shown);

function loadHistory(studentId, element) {
  const key = cacheKey(studentId, element);
  if (!pending.has(key)) {
    const request = organisationsAPI.getStudentApplicationHistory(studentId, element)
      .then((data) => {
        if (!isHistory(data)) throw new Error('history');
        loaded.set(key, data);
        return data;
      })
      .finally(() => pending.delete(key));
    pending.set(key, request);
  }
  return pending.get(key);
}

const stateFor = (key) => (loaded.has(key)
  ? { key, status: 'ready', data: loaded.get(key) }
  : { key, status: 'loading', data: null });

function useHistory(studentId, element) {
  const key = cacheKey(studentId, element);
  const [state, setState] = useState(() => stateFor(key));
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (loaded.has(key)) {
      setState(stateFor(key));
      return undefined;
    }
    let live = true;
    setState({ key, status: 'loading', data: null });
    loadHistory(studentId, element)
      .then((data) => { if (live) setState({ key, status: 'ready', data }); })
      .catch(() => { if (live) setState({ key, status: 'error', data: null }); });
    return () => { live = false; };
  }, [key, studentId, element, attempt]);

  // Until the effect has run for a new element, the state is the last one's.
  const current = state.key === key ? state : stateFor(key);
  return { ...current, retry: () => setAttempt((n) => n + 1) };
}

/** "A", "A and B", "A, B and C". */
function listOf(names) {
  if (names.length < 2) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function daysText(days) {
  if (days == null) return '';
  if (days <= 0) return ' on the same day';
  return ` in ${plural(days, 'day')}`;
}

const Summary = ({ history, from, to }) => {
  const labels = new Map(history.moves.map((m) => [m.key, m.label]));
  const never = history.never_shown.map((k) => labels.get(k)).filter(Boolean);
  let lead;
  if (to) {
    const s = pairSummary(history, from, to);
    lead = (
      <>
        <b>{`${s.from} → ${s.to} moves`}</b>
        {`${daysText(s.days)}, across ${plural(s.across, 'feedback request')}.`}
      </>
    );
  } else {
    lead = (
      <>
        <b>{`${from.moves_shown} of ${from.total_moves} moves`}</b>
        {` shown on ${dayMonth(from.at)}.`}
      </>
    );
  }
  return (
    <p className="td-act-vh__summary">
      {lead}
      {never.length ? (
        <>
          {' '}
          <b>{listOf(never)}</b>
          {` ${never.length === 1 ? "hasn't" : "haven't"} been shown in any feedback request yet.`}
        </>
      ) : null}
    </p>
  );
};

const DraftRow = ({ studentId, point, personal }) => {
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(false);
  const date = dayMonth(point.at);
  const open = async () => {
    setError('');
    setOpening(true);
    try {
      await openBuilderDocument(studentId, point.user_document_id);
    } catch (err) {
      setError(openDocumentErrorMessage(err));
    } finally {
      setOpening(false);
    }
  };
  return (
    <li className="td-act-vh__draft">
      <span>
        {`#${point.number} · feedback request · ${date}`}
        {point.score != null ? ` · score ${point.score}` : ''}
      </span>
      {personal && point.user_document_id ? (
        <button
          type="button"
          className="td-act-vh__open"
          onClick={open}
          disabled={opening}
          aria-label={`Open the draft and feedback from ${date} in a new tab`}
        >
          Open draft and feedback
          <span aria-hidden="true"> ↗</span>
        </button>
      ) : null}
      {error ? <small className="td-doc__error" role="alert">{error}</small> : null}
    </li>
  );
};

const Drafts = ({ studentId, history, documentType }) => {
  const drafts = [...feedbackPoints(history)].reverse();
  if (!drafts.length) return null;
  const personal = documentType === 'personal_statement';
  return (
    <>
      <div className="td-act-vh__eyebrow">Drafts from the application builder</div>
      <ul className="td-act-vh__drafts">
        {drafts.map((point) => (
          <DraftRow key={point.number} studentId={studentId} point={point} personal={personal} />
        ))}
      </ul>
      <p className="td-act-note">
        Saved each time the student asked for feedback: their text and the feedback they got, as a Word file.
      </p>
    </>
  );
};

const FileRow = ({ studentId, upload }) => {
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(false);
  const date = dayMonth(upload.at);

  const open = async () => {
    setError('');
    setOpening(true);
    try {
      await openDocument(studentId, upload.tracker_document_id);
    } catch (err) {
      setError(openDocumentErrorMessage(err));
    } finally {
      setOpening(false);
    }
  };

  return (
    <li className="td-act-vh__file">
      <span>{`Version ${upload.version_number} · uploaded ${date}`}</span>
      <button
        type="button"
        className="td-act-vh__open"
        onClick={open}
        disabled={opening}
        aria-label={`Open version ${upload.version_number}, uploaded ${date}, in a new tab`}
      >
        Open
        <span aria-hidden="true"> ↗</span>
      </button>
      {error ? <small className="td-doc__error" role="alert">{error}</small> : null}
    </li>
  );
};

/** The left column: the comparison, or why there is none. */
const Comparison = ({ history, picks }) => {
  const feedback = feedbackPoints(history);
  if (!feedback.length) {
    return (
      <div className="td-act-vh__empty">
        <p className="td-act-vh__empty-title">No feedback requests yet, so nothing to compare.</p>
        <p className="td-act-vh__empty-hint">
          Each time the student asks for feedback in the application builder, it appears here as a point to compare.
        </p>
      </div>
    );
  }
  if (!history.moves.length) {
    return (
      <div className="td-act-vh__empty">
        <p className="td-act-vh__empty-title">No writing moves are labelled for this part yet, so there is nothing to compare.</p>
        <p className="td-act-vh__empty-hint">The feedback requests and files are still listed.</p>
      </div>
    );
  }
  const byNumber = new Map(feedback.map((p) => [p.number, p]));
  const [from, to] = picks.map((n) => byNumber.get(n));
  if (!from) return null;
  let prompt = null;
  if (!to) {
    prompt = feedback.length === 1
      ? 'Compare needs two feedback requests.'
      : 'Pick a second feedback point to compare it with.';
  }
  return (
    <>
      <MoveComparison moves={history.moves} from={from} to={to} />
      {prompt ? <p className="td-act-meta">{prompt}</p> : null}
    </>
  );
};

const Ready = ({ studentId, history, picks, onPick, documentType }) => {
  const feedback = feedbackPoints(history);
  const files = uploadPoints(history);
  if (!history.points.length) {
    return <p className="td-act-meta">Nothing recorded for this part yet: no feedback requests and no uploaded files.</p>;
  }
  const byNumber = new Map(feedback.map((p) => [p.number, p]));
  const [from, to] = picks.map((n) => byNumber.get(n));
  return (
    <>
      {feedback.length > 1 ? (
        <p className="td-act-vh__howto">
          Click two feedback points to compare them; it opens on the first and the latest. Uploaded files have no move
          data, so open them from the list.
        </p>
      ) : null}
      <VersionTimeline points={history.points} picks={picks} onPick={onPick} label={`Versions of ${history.label}`} />
      <div className="td-act-vh__cmp">
        <div>
          <Comparison history={history} picks={picks} />
        </div>
        <div>
          {from && history.moves.length ? (
            <>
              <div className="td-act-vh__eyebrow">Summary</div>
              <Summary history={history} from={from} to={to} />
            </>
          ) : null}
          <Drafts studentId={studentId} history={history} documentType={documentType} />
          <div className="td-act-vh__eyebrow">Files</div>
          {files.length ? (
            <ul className="td-act-vh__files">
              {files.map((u) => <FileRow key={u.number} studentId={studentId} upload={u} />)}
            </ul>
          ) : <p className="td-act-meta">No files uploaded yet.</p>}
          <p className="td-act-note">
            Writing moves are detected by matching phrases in each draft the student asked feedback on. A guide, not a
            mark. A feedback request where no writing move was detected isn&apos;t shown as a point.
          </p>
        </div>
      </div>
    </>
  );
};

/**
 * One application document's version history, opened inline from its card
 * (GET …/activity/applications/<element>/history, all time). `elements`
 * are the document's parts ({ key, label }): the personal statement's three
 * questions get tabs (Segments); the CV and cover letter have one part and
 * none. `initialElement` is the part to open on (the first by default).
 * Each part's history is fetched the first time it is shown and kept for
 * the page's life. Feedback points open with the first and the latest
 * picked; a click starts a new pair. Files open through the staff link
 * route (overview/cells openDocument). `id` is the panel's, for the
 * card button's aria-controls.
 */
const DocumentHistory = ({
  studentId, documentType, elements, initialElement, id, hidden = false,
}) => {
  const [element, setElement] = useState(
    elements.some((e) => e.key === initialElement) ? initialElement : elements[0]?.key,
  );
  const [picksByElement, setPicksByElement] = useState({});
  const history = useHistory(studentId, element);
  const data = history.status === 'ready' ? history.data : null;
  const picks = picksByElement[element] || (data ? defaultPicks(data) : []);

  const onPick = (number) => setPicksByElement((prev) => ({ ...prev, [element]: nextPicks(picks, number) }));

  let body;
  if (history.status === 'error') {
    body = (
      <div className="td-act-vh__failed" role="alert">
        <p>Couldn&apos;t load the history. Try again.</p>
        <button type="button" className="td-btn td-btn--small" onClick={history.retry}>Retry</button>
      </div>
    );
  } else if (!data) {
    body = <p className="td-act-meta" role="status">Loading the version history…</p>;
  } else {
    body = (
      <Ready
        studentId={studentId}
        history={data}
        picks={picks}
        onPick={onPick}
        documentType={documentType}
      />
    );
  }

  return (
    <section
      className="td-act-vh"
      id={id}
      hidden={hidden || undefined}
      aria-label={`${documentTypeLabel(documentType) || 'Document'} version history`}
    >
      {elements.length > 1 ? (
        <Segments
          label="Part"
          options={elements.map((e) => ({ key: e.key, label: e.label }))}
          value={element}
          onChange={setElement}
        />
      ) : null}
      {body}
    </section>
  );
};

export default DocumentHistory;
